import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Modal, Transfer } from 'antd';
import {
  fetchPermissionGroupList,
  selectContext,
  selectPermissionGroups,
} from '@icp/form-renderer-core';
import { resolveVariablePattern } from '@icp/utils';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAppContext } from '@icp/app';
import { withFieldWrapper } from '../../fieldWrapper';
import { useDispatch, useSelector, useStore } from '../../store';
import { useClassName } from '../../hooks';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { ConditionalPropertyPropType } from '../../propTypes';
import { ButtonUI } from '../ButtonElement';
import { Permissions } from '../index';

const DEFAULT_STYLE_HEIGHT = 'calc(80vh - 64px)';

const PermissionGroupsElement = forwardRef(function PermissionGroupsElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    style,
    componentProps = {},
    value,
    onChange,
    readonly,
    disabled,
  } = props;

  const {
    style: compStyle,
    className: compClassNameProp,
    projectId: projectIdPattern,
  } = componentProps;

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(compClassNameProp);
  const ElementDecorator = useElementDecorator();
  const { t } = useTranslation(['icp-form-renderer']);
  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  const [targetKeys, setTargetKeys] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [openedGroup, setOpenedGroup] = useState(null);
  const store = useStore();
  const context = selectContext(store.getState());

  const appCtx = useAppContext();
  const pbc = appCtx?.pbc;
  const resolvedProjectId = projectIdPattern
    ? resolveVariablePattern({ context, pattern: projectIdPattern })
    : null;
  const projectId = resolvedProjectId || (pbc?.projectToken !== 'ceta' ? pbc?.projectId : null);
  const isInDesign = useIsInDesign();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isInDesign || !projectId) {
      return () => {};
    }
    const p = dispatch(fetchPermissionGroupList({ projectId }));
    return () => {
      p.abort();
    };
  }, [dispatch, isInDesign, projectId]);

  const permissionGroups = useSelector(selectPermissionGroups);
  const dataSource = useMemo(
    () =>
      permissionGroups.map((permGroup) => ({
        key: permGroup.id,
        title: permGroup.name,
        data: permGroup,
      })),
    [permissionGroups],
  );

  useEffect(() => {
    if (targetKeys == null && value != null) {
      setTargetKeys(value.map((permGroup) => permGroup.id));
    }
  }, [targetKeys, value]);

  const handleChange = (newTargetKeys) => {
    setTargetKeys(newTargetKeys);
    const targetKeySet = new Set(newTargetKeys);
    const selectedGroups = permissionGroups.filter((permGroup) => targetKeySet.has(permGroup.id));
    onChange(selectedGroups);
  };

  const handleSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
    setCheckedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx('permission-groups-element', className, classNameComp)}
        style={style}
        ref={nodeRef}
      >
        <Transfer
          titles={[t('permission-groups.unselected'), t('permission-groups.selected')]}
          dataSource={dataSource}
          targetKeys={targetKeys}
          selectedKeys={checkedKeys}
          onChange={handleChange}
          onSelectChange={handleSelectChange}
          disabled={readonly || disabled}
          listStyle={{
            width: compStyle?.width || '50%',
            height: compStyle?.height || DEFAULT_STYLE_HEIGHT,
          }}
          render={(item) => (
            <div className="permission-groups-item">
              <span className="permission-groups-item-title">{item.title}</span>
              <ButtonUI
                type="text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenedGroup(item.data);
                }}
              >
                {t('permission-groups.item-details')}
              </ButtonUI>
            </div>
          )}
        />
        {openedGroup ? (
          <Modal
            title={t('permission-groups.item-details-dialog-title', {
              group: openedGroup.name,
            })}
            centered={true}
            open={true}
            footer={null}
            onCancel={() => setOpenedGroup(null)}
            width="60vw"
          >
            <Permissions
              value={openedGroup.permissions}
              readonly={true}
              componentProps={{
                dataUrl: `/user-management/api/permission/list-by-project-id/${projectId}`,
                dataResponseKeyPath: '',
              }}
            />
          </Modal>
        ) : null}
      </div>
    </ElementDecorator>
  );
});

PermissionGroupsElement.displayName = 'PermissionGroups';

PermissionGroupsElement.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  componentProps: PropTypes.shape({
    projectId: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.shape({}),
  }),
  value: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func,
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
};

export default withFieldWrapper(PermissionGroupsElement);
